/**AA
 * Delete Account Page
 * Route: /delete
 * Allows users to permanently delete their account without password verification
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  Trash2, 
  Mail, 
  Phone, 
  AlertTriangle, 
  ArrowLeft,
  Loader2,
  CheckCircle,
  XCircle
} from 'lucide-react';
import axios from 'axios';

const API = '/api/v1';

const DeleteAccount = () => {
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  
  // Form states
  const [identifier, setIdentifier] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [step, setStep] = useState(1); // 1: Form, 2: Confirmation, 3: Success
  
  // UI states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showReasons, setShowReasons] = useState(false);

  const CONFIRM_PHRASE = 'DELETE MY ACCOUNT';

  // Handle delete account
  const handleDeleteAccount = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validate identifier
    if (!identifier.trim()) {
      setError('Please enter your phone number or email');
      return;
    }

    // Validate confirmation text
    if (confirmText !== CONFIRM_PHRASE) {
      setError(`Please type "${CONFIRM_PHRASE}" to confirm`);
      return;
    }

    // Clean identifier
    const cleanIdentifier = identifier.includes('@') 
      ? identifier.trim() 
      : identifier.replace(/\D/g, '');

    setLoading(true);
    try {
      const response = await axios.delete(`${API}/auth/delete-account`, {
        data: { 
          identifier: cleanIdentifier
          // ❌ Password removed
        }
      });

      console.log('✅ Account deleted:', response.data);

      // Clear all user data
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('cached_user');
      
      // Logout user
      logout();
      
      setSuccess('✅ Your account has been permanently deleted.');
      setStep(3); // Success step
      
      // Redirect to home after 3 seconds
      setTimeout(() => {
        navigate('/');
      }, 3000);
      
    } catch (err) {
      console.error('❌ Delete account error:', err.response?.data);
      
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.detail || 
                          'Something went wrong. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Go back
  const handleGoBack = () => {
    navigate(-1);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-red-100 rounded-full mb-4">
            <Trash2 className="w-10 h-10 text-red-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900">Delete Account</h1>
          <p className="text-gray-600 mt-2">
            This action is <span className="text-red-600 font-semibold">permanent</span> and cannot be undone
          </p>
        </div>

        {/* Back Button */}
        <button
          onClick={handleGoBack}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition"
        >
          <ArrowLeft size={20} />
          <span>Go Back</span>
        </button>

        {/* Error Alert */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 flex items-start gap-3">
            <XCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
            <span className="text-sm font-medium">{error}</span>
          </div>
        )}

        {/* Success Alert */}
        {success && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-6 flex items-start gap-3">
            <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
            <span className="text-sm font-medium">{success}</span>
          </div>
        )}

        {/* Step 1: Delete Form */}
        {step === 1 && (
          <div className="bg-white rounded-xl shadow-lg p-6 md:p-8">
            <form onSubmit={handleDeleteAccount}>
              {/* Warning Box */}
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-red-700">
                    <p className="font-semibold">⚠️ Warning: This will permanently delete:</p>
                    <ul className="list-disc list-inside mt-2 space-y-1 text-red-600">
                      <li>Your profile and personal information</li>
                      <li>All your bookings and service history</li>
                      <li>KoPartner profile (if any)</li>
                      <li>All uploaded documents and media</li>
                      <li>Payment and transaction history</li>
                      <li>All your data from the platform</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Identifier Input */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number or Email <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  {identifier.includes('@') ? (
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  ) : (
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  )}
                  <input
                    type="text"
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    placeholder="Enter your registered phone or email"
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition"
                    disabled={loading}
                    required
                    autoFocus
                  />
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  Enter the phone number or email linked to your account
                </p>
              </div>

              {/* Confirmation Input */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type <span className="text-red-600 font-bold">{CONFIRM_PHRASE}</span> to confirm <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
                  placeholder={`Type "${CONFIRM_PHRASE}"`}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition font-mono"
                  disabled={loading}
                  required
                />
                {confirmText && confirmText !== CONFIRM_PHRASE && (
                  <p className="text-red-500 text-xs mt-1">
                    Please type exactly: {CONFIRM_PHRASE}
                  </p>
                )}
                {confirmText === CONFIRM_PHRASE && (
                  <p className="text-green-600 text-xs mt-1 flex items-center gap-1">
                    <CheckCircle size={14} /> Confirmation matched
                  </p>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={handleGoBack}
                  className="flex-1 py-3 border border-gray-300 rounded-lg font-semibold hover:bg-gray-50 transition"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={
                    loading || 
                    !identifier.trim() || 
                    confirmText !== CONFIRM_PHRASE
                  }
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition"
                >
                  {loading ? (
                    <><Loader2 className="animate-spin" size={18} /> Deleting...</>
                  ) : (
                    <><Trash2 size={18} /> Permanently Delete Account</>
                  )}
                </button>
              </div>

              {/* Additional Info */}
              <div className="mt-6 text-center">
                <button
                  type="button"
                  onClick={() => setShowReasons(!showReasons)}
                  className="text-sm text-gray-500 hover:text-gray-700 transition"
                >
                  {showReasons ? 'Hide' : 'Show'} reasons to keep your account
                </button>
                {showReasons && (
                  <div className="mt-3 p-4 bg-blue-50 rounded-lg text-left">
                    <p className="text-sm text-gray-600">
                      <span className="font-semibold">💡 Before you go:</span>
                    </p>
                    <ul className="text-sm text-gray-600 mt-2 space-y-1 list-disc list-inside">
                      <li>Your profile and data cannot be recovered</li>
                      <li>You will lose access to all services</li>
                      <li>Any pending bookings will be cancelled</li>
                      <li>KoPartner earnings and history will be lost</li>
                      <li>You can create a new account anytime</li>
                    </ul>
                  </div>
                )}
              </div>
            </form>
          </div>
        )}

        {/* Step 2: Loading/Processing */}
        {step === 2 && (
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <Loader2 className="w-16 h-16 text-red-600 animate-spin mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-800">Deleting Your Account...</h2>
            <p className="text-gray-500 mt-2">Please wait while we process your request</p>
          </div>
        )}

        {/* Step 3: Success */}
        {step === 3 && (
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-4">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800">Account Deleted</h2>
            <p className="text-gray-600 mt-2">
              Your account has been permanently deleted.
            </p>
            <p className="text-gray-400 text-sm mt-4">
              Redirecting to home page...
            </p>
            <button
              onClick={() => navigate('/')}
              className="mt-6 px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
            >
              Go to Home
            </button>
          </div>
        )}

        {/* Footer */}
        <div className="text-center mt-8">
          <p className="text-xs text-gray-400">
            Need help? Contact our support team at{' '}
            <a href="mailto:support@kopartner.com" className="text-red-600 hover:underline">
              support@kopartner.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default DeleteAccount;
